<template>
  <div class="cc-chat-panel">
    <div class="cc-chat-panel-messages" ref="messagesContainer">
      <template v-for="(message, index) in messages">
        <system-message v-if="message.role === 'system'" :key="'s-' + index" :text="message.text" :content="message.content" :timestamp="message.timestamp" :is-last="index === messages.length - 1" :streaming="!!message.streaming" @action="handleAction($event, index)" />
        <user-message v-else-if="message.role === 'user'" :key="'u-' + index" :text="message.text" :initials="userInitials" :timestamp="message.timestamp" />
      </template>
    </div>
    <chat-input-bar
      ref="inputBar"
      :loading="loading"
      :locked="context.step === 'done'"
      locked-text="Chat closed — campaign complete. Start a new chat to create another."
      @send="handleSend"
    />
  </div>
</template>

<script>
import ChatInputBar from '../../../common/chat-panel/ChatInputBar.vue'
import SystemMessage from '../../../common/chat-panel/SystemMessage.vue'
import UserMessage from '../../../common/chat-panel/UserMessage.vue'
import api from '../../../../../utils/api'
import { getAccessToken } from '../../../../../utils/auth'
import Papa from 'papaparse'
import templateMediaUpload from '../../templateMediaUpload'

// Maps the backend's step string (nudgeCampaignChat.js) to the 4-step progress bar
// used by trigger-based flows (always-on, in-session — no separate audience-build
// step, no send window). Batch OOS keeps the original 5-step numbering.
// GET /ai-console-chat?last=true returns the single most recent chat for the user
// ACROSS EVERY FEATURE (there's no chat_source/feature column on that endpoint) — so we
// fetch the user's recent list unfiltered and pick out our own by this purpose string,
// instead of blindly resuming whatever chat (e.g. an Entry Point one) happens to be newest.
const CHAT_PURPOSE = 'Nudge Console — Create Campaign'

// The statistics service doesn't reliably hand `is_completed` back as a real JS
// boolean (observed as the string "false"/"true", not just `false`/`true`) — since
// any non-empty string is truthy, `!chat.is_completed` was always false for a string
// value regardless of what it said, so every incomplete chat looked "completed" and
// every reload silently created a new one instead of resuming. Compare explicitly.
const isChatIncomplete = chat => {
  const v = chat && chat.is_completed
  return v !== true && v !== 'true' && v !== 1 && v !== '1' && v !== 't'
}

const STEP_NUMBER = {
  start: 2,
  // Country/customer are no longer separate steps — a single journey pick sets all three
  // (see nudgeCampaignChat.js's journeyComboBlock/journeysForStep). The manual escape
  // hatch (journey_manual_*) still counts as this same step 2.
  journey: 2,
  journey_manual_country: 2,
  journey_manual_customer: 2,
  journey_manual_journey: 2,
  channel: 2,
  language: 2,
  trigger_type: 2,
  trigger_stage: 2,
  trigger_stage_audience: 2,
  trigger_window_kind: 2,
  trigger_window_value: 2,
  trigger_max_nudges: 2,
  trigger_add_more: 2,
  insession_window1: 2,
  insession_second_nudge_offer: 2,
  insession_window2: 2,
  insession_max_nudges: 2,
  content_template: 3,
  content_goal: 3,
  content_tone: 3,
  content: 3,
  content_template_var_source: 3,
  content_template_var_values: 3,
  content_template_header: 3,
  content_template_media: 3,
  content_template_media_same: 3,
  content_template_media_filename: 3,
  content_template_create: 3,
  content_ab_offer: 3,
  content_ab_template: 3,
  content_ab_review: 3,
  ab_split: 3,
  content_destination: 3,
  content_destination_pick: 3,
  content_ab_destination: 3,
  content_ab_destination_pick: 3,
  review: 4,
  done: 4
}

// Batch OOS's own 5-step numbering (Nudge Type / Audience / Content / Send Window /
// Review) — content_* step names are shared with the map above, so this only needs its
// own entries for the steps that don't exist in the other two flows.
const BATCH_STEP_NUMBER = {
  ...STEP_NUMBER,
  audience_source: 2,
  audience_saved_pick: 2,
  audience_build_new: 2,
  audience_upload: 2,
  audience_confirm: 2,
  content_template: 3,
  content_goal: 3,
  content_tone: 3,
  content: 3,
  content_ab_offer: 3,
  content_ab_template: 3,
  content_ab_review: 3,
  ab_split: 3,
  content_destination: 3,
  content_destination_pick: 3,
  content_ab_destination: 3,
  content_ab_destination_pick: 3,
  sendwindow_suggest: 4,
  sendwindow_pick_daytime: 4,
  sendwindow_pick_date: 4,
  sendwindow_custom_confirm: 4,
  sendwindow_blackout_confirm: 4,
  review: 5,
  done: 5
}

export default {
  name: 'CampaignChatPanel',
  components: { ChatInputBar, SystemMessage, UserMessage },
  // uploadTemplateMedia(): a template header file uploaded in the chat.
  mixins: [templateMediaUpload],
  props: {
    // Set when the Dashboard's Drafts tab linked here with ?chatId= — resume exactly that
    // draft rather than whichever incomplete chat for this purpose happens to be newest.
    resumeChatId: { type: [String, Number], default: null }
  },
  emits: ['summary-change'],
  data () {
    const username = localStorage.getItem('username') || ''
    const parts = username.split('.')
    const firstName = parts[0] ? parts[0].replace(/\b\w/g, c => c.toUpperCase()) : ''
    const initials = parts
      .filter(p => p.length > 0)
      .map(p => p[0].toUpperCase())
      .slice(0, 2)
      .join('')
    return {
      loading: false,
      userInitials: initials || 'U',
      userId: username,
      firstName,
      chatId: null,
      messageSequence: 0,
      messages: [],
      // Which of the 3 nudge types the user picked in message 0 — null until then.
      // Only 'always-on' has a real backend today; the other two are stubbed.
      nudgeType: null,
      // Mirrors the backend's context object turn-for-turn so it can be resent on
      // every request (the backend is stateless between calls).
      context: {},
      summary: { type: null, channels: [] }
    }
  },
  watch: {
    messages () {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  },
  async mounted () {
    await this.initChat()
    this.$emit('summary-change', { step: this.nudgeType ? (STEP_NUMBER[this.context.step] || 2) : 1, summary: this.summary })
    // PRD §3.2.5: periodic campaign-state snapshot so a draft survives more than just a
    // page refresh — independent of the explicit "Save as draft" button.
    this._autosaveTimer = setInterval(() => {
      if (this.context.step && this.context.step !== 'done') this.persistState()
    }, 60000)
  },
  beforeDestroy () {
    if (this._autosaveTimer) clearInterval(this._autosaveTimer)
  },
  methods: {
    // Best-effort analytics (§4.3) — never blocks or breaks the chat if it fails.
    async trackEvent (eventName, properties = {}) {
      try {
        await api.buildRequest('ai-console-events', 'post', {
          event_name: eventName,
          user_id: this.userId,
          ai_console_chat_id: this.chatId,
          instance: 'nudge-console-create-campaign',
          properties
        })
      } catch (err) { /* analytics only, swallow */ }
    },
    greetingContent () {
      return [
        { type: 'text', value: `👋 Hi ${this.firstName}! What kind of nudge campaign do you want to create?` },
        {
          type: 'chips',
          items: [
            { label: 'Re-engage mid-journey users', subtitle: 'Batch out-of-session', value: 'batch' },
            { label: 'Set up an always-on trigger', subtitle: 'Fires on stage / inactivity', value: 'always-on', variant: 'teal' },
            { label: 'Configure an in-session nudge', subtitle: 'Fires mid-conversation', value: 'in-session', variant: 'teal' }
          ]
        }
      ]
    },

    async initChat () {
      if (this.resumeChatId) {
        try {
          const { data } = await api.buildRequest(`ai-console-chat/${this.resumeChatId}`, 'get')
          const payload = data && data.payload
          const session = Array.isArray(payload) ? payload[0] : payload
          if (session && session.id && session.purpose === CHAT_PURPOSE && isChatIncomplete(session)) {
            await this.resumeSession(session)
            return
          }
        } catch (err) {
          // Stale/bad link — fall through to the normal lookup below instead of stranding
          // the user on an empty chat.
        }
      }

      try {
        const { data: listData } = await api.buildRequest('ai-console-chat', 'get', { user_id: this.userId, last: 'true' })
        const list = listData && listData.payload && (Array.isArray(listData.payload) ? listData.payload : [listData.payload])
        // Server returns the list newest-first — only the single most recent chat for
        // this purpose matters here. If it's already done, start fresh rather than
        // reaching further back for an older incomplete one.
        const last = (list || []).find(c => c.purpose === CHAT_PURPOSE)

        if (last && isChatIncomplete(last)) {
          await this.resumeSession(last)
        } else {
          await this.createChat()
        }
      } catch (err) {
        // Falls through to createChat() below, which itself resumes rather than
        // duplicating if the server finds an existing draft — see createChat().
        await this.createChat()
      }
    },

    async resumeSession (session) {
      this.chatId = session.id
      const saved = session.summary || {}
      this.nudgeType = saved.nudgeType || null
      this.context = saved.context || {}
      await this.restoreHistory()
      if (this.nudgeType) this.updateSummaryFromContext()
    },

    async createChat () {
      const greeting = { role: 'system', content: this.greetingContent(), timestamp: Date.now() }
      try {
        const { data } = await api.buildRequest('ai-console-chat', 'post', {
          user_id: this.userId,
          purpose: CHAT_PURPOSE
        })
        const session = data && data.payload
        if (session && session.id) {
          // The server find-or-creates: if an incomplete draft for this user/purpose
          // already existed (e.g. this call was itself a retry after a flaky GET, or a
          // second tab raced us), it hands that session back instead of inserting a
          // new row — resume it exactly like initChat() would, don't restart the chat.
          if (data.resumed) {
            await this.resumeSession(session)
            return
          }
          this.chatId = session.id
          this.messages = [greeting]
          await this.saveTurn('bot', this.userId, JSON.stringify(greeting.content))
          this.trackEvent('campaign_wizard_started')
          return
        }
      } catch (err) {
        console.error('Failed to create chat session', err)
      }
      this.messages = [greeting]
    },

    async restoreHistory () {
      try {
        const { data } = await api.buildRequest(`ai-console-chat/${this.chatId}/history`, 'get')
        const history = data && data.payload
        if (!Array.isArray(history) || !history.length) return

        this.messageSequence = history[history.length - 1].message_sequence
        this.messages = history.map(turn => {
          const isBot = turn.sender === 'bot'
          let content = null
          let text = turn.message

          if (isBot && turn.message) {
            try {
              const parsed = JSON.parse(turn.message)
              if (Array.isArray(parsed)) { content = parsed; text = null }
            } catch (_) {}
          }

          return {
            role: isBot ? 'system' : 'user',
            text,
            content,
            timestamp: turn.created_at ? new Date(turn.created_at).getTime() : Date.now()
          }
        })
      } catch (err) {
        console.error('Failed to restore chat history', err)
      }
    },

    // Called by CreateCampaign.vue right before it remounts a fresh ChatPanel for
    // "+ New Chat". Without this, the fresh instance's initChat() would just find this
    // same not-yet-completed session again (it's still the newest one with our purpose)
    // and silently restore it instead of starting over.
    async abandonChat () {
      if (!this.chatId) return
      try {
        await api.buildRequest(`ai-console-chat/${this.chatId}`, 'put', { is_completed: true })
      } catch (err) {
        console.error('Failed to abandon chat session', err)
      }
    },

    async saveTurn (sender, receiver, message) {
      if (!this.chatId) return
      this.messageSequence += 1
      try {
        await api.buildRequest(`ai-console-chat/${this.chatId}`, 'put', {
          turn: { sender, receiver, message, message_sequence: this.messageSequence }
        })
      } catch (err) {
        console.error('Failed to save turn', err)
      }
    },

    // Persists nudgeType + the backend's context object so a page refresh mid-flow
    // can resume from where it left off, not just replay old messages read-only.
    async persistState () {
      if (!this.chatId) return
      try {
        await api.buildRequest(`ai-console-chat/${this.chatId}`, 'put', {
          summary: { nudgeType: this.nudgeType, context: this.context },
          is_completed: this.context.step === 'done'
        })
      } catch (err) {
        console.error('Failed to persist chat state', err)
      }
    },

    appendUserMessage (text) {
      this.messages.push({ role: 'user', text, timestamp: Date.now() })
      this.saveTurn(this.userId, 'bot', text)
    },

    appendBotMessage (content) {
      this.messages.push({ role: 'system', content, timestamp: Date.now() })
      this.saveTurn('bot', this.userId, JSON.stringify(content))
    },

    // Turns a clicked action back into the sentence we show as the user's own message
    // (mirrors what the user effectively "said" by clicking).
    resolveDisplayText (messageIndex, action) {
      const { type, value, elementIndex } = action
      const block = this.messages[messageIndex] && this.messages[messageIndex].content && this.messages[messageIndex].content[elementIndex]

      if (type === 'chip-multi-confirm') {
        return Array.isArray(value) ? value.join(', ') : String(value)
      }
      if (type === 'card-confirm' && block && Array.isArray(block.items)) {
        const card = block.items[value]
        return card ? card.title : String(value)
      }
      if ((type === 'chip' || type === 'button') && block && Array.isArray(block.items)) {
        const item = block.items.find(it => (typeof it === 'string' ? it : it.value) === value)
        if (item) return typeof item === 'string' ? item : item.label
      }
      if (type === 'content-confirm') return 'Save & continue'
      if (type === 'content-regenerate') return value && String(value).trim() ? `Regenerate: ${String(value).trim()}` : 'Regenerate'
      if (type === 'form') {
        const vals = Object.values(value || {}).filter(v => v !== '' && v != null)
        return vals.length ? vals.join(', ') : ''
      }
      return value === undefined || value === null ? '' : String(value)
    },

    handleAction (action, messageIndex) {
      if (this.loading) return

      if (!this.nudgeType) {
        if (action.type !== 'chip') return
        const label = this.resolveDisplayText(messageIndex, action)
        this.nudgeType = action.value
        this.summary = { ...this.summary, type: label }
        this.$emit('summary-change', { step: 1, summary: this.summary })
        this.appendUserMessage(label)
        this.context.nudgeType = action.value
        this.persistState()
        this.sendToBackend({})
        return
      }

      // Batch OOS's "upload a list" branch (flow-batch-oos.md §Step 2, Branch C) — the
      // upload block hands back a raw browser File, not something the backend (stateless,
      // no multipart handling) can accept. Parse client-side with PapaParse, same pattern
      // UploadAudiencePanel.vue uses for Audience Builder's own CSV upload, and send the
      // parsed account IDs as the resolved value instead of the file itself.
      if (action.type === 'upload') {
        const block = ((this.messages[messageIndex] || {}).content || [])[action.elementIndex] || {}
        const isTemplateMedia = block.upload && block.upload.kind === 'template-media'
        // Upload boxes stay clickable in older messages; only the live media step takes one.
        if (isTemplateMedia && (messageIndex !== this.messages.length - 1 || this.context.step !== 'content_template_media')) return
        this.appendUserMessage(action.value && action.value.name ? action.value.name : 'Uploaded file')
        if (isTemplateMedia) this.uploadTemplateMedia(action.value, block.upload)
        else this.parseUploadedList(action.value)
        return
      }
      if (action.type === 'upload-skip') {
        this.appendUserMessage('Skip')
        this.sendToBackend({ action: { ...action, type: 'upload-skip' } })
        return
      }

      const label = this.resolveDisplayText(messageIndex, action)
      this.appendUserMessage(label)
      if (action.value === 'activate') this.trackEvent(this.nudgeType === 'batch' ? 'campaign_scheduled' : 'campaign_activated')
      else if (action.value === 'save_draft') this.trackEvent('campaign_draft_saved')
      else if (action.type === 'content-confirm') this.trackEvent('campaign_content_edited')
      else if (action.type === 'card-confirm' && this.context.step === 'audience_saved_pick') this.trackEvent('campaign_segment_selected', { selection_method: 'saved' })
      this.sendToBackend({ action })
    },

    parseUploadedList (file) {
      if (!file) return
      this.loading = true
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: h => h.trim().toLowerCase(),
        complete: (results) => {
          this.loading = false
          const rows = results.data || []
          const idKey = Object.keys(rows[0] || {}).find(k => /account|phone|user|id/i.test(k)) || Object.keys(rows[0] || {})[0]
          const accountIds = rows.map(r => (idKey ? r[idKey] : null)).filter(Boolean)
          if (!accountIds.length) {
            this.appendBotMessage([{ type: 'text', value: "Couldn't find any identifiers in that file — make sure it has a column with account IDs or phone numbers, then try again." }])
            return
          }
          this.trackEvent('campaign_segment_selected', { selection_method: 'uploaded' })
          this.sendToBackend({ action: { type: 'upload', value: accountIds, fileName: file.name } })
        },
        error: (err) => {
          this.loading = false
          this.appendBotMessage([{ type: 'text', value: `Couldn't read that file: ${err.message || 'unknown error'}. Please try again.` }])
        }
      })
    },

    handleSend (text) {
      if (!text || this.loading) return

      if (!this.nudgeType) {
        this.appendUserMessage(text)
        const lower = text.toLowerCase()
        const isInSession = lower.includes('in-session') || lower.includes('in session') || lower.includes('conversation')
        const isBatch = !isInSession && (lower.includes('batch') || lower.includes('re-engage') || lower.includes('reengage') || lower.includes('mid-journey') || lower.includes('mid journey'))
        const isAlwaysOn = !isInSession && !isBatch && (lower.includes('always') || lower.includes('trigger'))
        if (isInSession || isBatch || isAlwaysOn) {
          this.nudgeType = isInSession ? 'in-session' : (isBatch ? 'batch' : 'always-on')
          this.summary = { ...this.summary, type: isInSession ? 'Configure an in-session nudge' : (isBatch ? 'Re-engage mid-journey users' : 'Set up an always-on trigger') }
          this.$emit('summary-change', { step: 1, summary: this.summary })
          this.context.nudgeType = this.nudgeType
          this.persistState()
          this.sendToBackend({})
          return
        }
        this.appendBotMessage([{ type: 'text', value: 'Please choose one of the options above to get started.' }])
        return
      }

      this.appendUserMessage(text)
      this.sendToBackend({ message: text })
    },

    async sendToBackend ({ action = null, message = null } = {}) {
      this.loading = true
      const streamIdx = this.messages.length
      this.messages.push({ role: 'system', content: null, timestamp: Date.now(), streaming: true })

      try {
        const host = window.location.hostname + (window.location.hostname === 'localhost' ? ':8080' : '')
        const response = await fetch(`${window.location.protocol}//${host}/api/nudge-campaign-chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}`, 'X-User-Email': localStorage.getItem('email') || '' },
          body: JSON.stringify({ context: this.context, action, message, chat_id: this.chatId, user_id: this.userId })
        })

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let sseBuffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          sseBuffer += decoder.decode(value, { stream: true })
          const lines = sseBuffer.split('\n')
          sseBuffer = lines.pop()
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const event = JSON.parse(line.slice(6))
            if (event.type === 'done') {
              const prevStep = this.context.step
              if (event.context) this.context = event.context
              this.messages.splice(streamIdx, 1, { role: 'system', content: event.content, timestamp: Date.now(), streaming: false })
              this.saveTurn('bot', this.userId, JSON.stringify(event.content))
              if (this.context.step === 'content_goal' && prevStep !== 'content_goal') this.trackEvent('campaign_trigger_configured')
              if (this.context.step === 'content' && prevStep !== 'content') this.trackEvent('campaign_content_generated')
              this.updateSummaryFromContext()
              this.persistState()
            } else if (event.type === 'error') {
              console.error('[nudge-campaign-chat] server error:', event.msg)
              this.messages.splice(streamIdx, 1, { role: 'system', text: `⚠️ ${event.msg || 'Something went wrong.'}`, timestamp: Date.now(), streaming: false })
            }
          }
        }
      } catch (err) {
        console.error('[nudge-campaign-chat] request failed:', err)
        this.messages.splice(streamIdx, 1, { role: 'system', text: `⚠️ ${err.message || 'Something went wrong.'} (check the browser console / server log for details)`, timestamp: Date.now(), streaming: false })
      } finally {
        this.loading = false
      }
    },

    updateSummaryFromContext () {
      const c = this.context
      const isBatch = c.nudgeType === 'batch'
      const triggers = c.triggers || []
      const triggerSummary = triggers.length
        ? triggers.map(t => {
          if (t.type === 'stage') return `Stage reached: ${t.stage}${t.audienceName ? `, audience: ${t.audienceName}` : ''}`
          if (t.type === 'in_session_inactivity') return `In-session inactivity: ${t.window1}${t.window2 ? ` / ${t.window2}` : ''}, max ${t.maxNudgesPerSession}/session`
          return `Inactivity (${t.windowKind}-window): ${t.windowValue}`
        }).join(' OR ')
        : null

      const typeLabel = c.nudgeType === 'in-session'
        ? 'Configure an in-session nudge'
        : (isBatch ? 'Re-engage mid-journey users' : 'Set up an always-on trigger')

      this.summary = {
        type: typeLabel,
        country: c.country || null,
        customer: c.customer || null,
        journey: c.journey ? c.journey.name : null,
        channels: c.channels || [],
        languages: c.languages || [],
        trigger: isBatch ? (c.audience ? `Segment: ${c.audience.name} (${c.audience.totalCount != null ? c.audience.totalCount : '—'} users)` : null) : triggerSummary,
        sendWindow: isBatch && c.sendWindow ? c.sendWindow.label : null,
        contentReady: !!c.contentByLanguage,
        guardrailsPassed: c.step === 'review' || c.step === 'done',
        hideSendWindow: !isBatch,
        finishLabel: isBatch ? 'Approve & Schedule' : 'Activate Campaign'
      }
      const stepMap = isBatch ? BATCH_STEP_NUMBER : STEP_NUMBER
      this.$emit('summary-change', { step: stepMap[c.step] || 2, summary: this.summary })
    }
  }
}
</script>
<style scoped src="./ChatPanel.css"></style>



// WEBPACK FOOTER //
// ChatPanel.vue?5015db3c